/** Same exclusions as `speculationRulesPayload` (quicklink fallback path). */
const quicklinkIgnores = [
  /wp-admin/,
  /wp-content\/uploads/,
  /\?.+/,
  (uri, elem) =>
    elem.relList?.contains?.('nofollow') ??
    String(elem.getAttribute('rel') || '')
      .split(/\s+/)
      .includes('nofollow'),
  (uri, elem) => Boolean(elem.closest('.no-prefetch')),
  (uri, elem) => elem.hasAttribute('data-skip-prefetch'),
  (uri, elem) => {
    try {
      return new URL(elem.href).origin !== window.location.origin
    } catch {
      return true
    }
  },
  (uri) => /\.(pdf|zip|docx?|xlsx?|jpe?g|png|gif)(\?|$)/i.test(uri)
]

const speculationRulesPayload = {
  prefetch: [
    {
      source: 'document',
      where: {
        and: [
          { href_matches: '/*' },
          { not: { href_matches: '/wp-*.php' } },
          { not: { href_matches: '/wp-admin/*' } },
          { not: { href_matches: '/wp-content/uploads/*' } },
          { not: { href_matches: '/*\\?(.+)' } },
          { not: { selector_matches: 'a[rel~="nofollow"]' } },
          { not: { selector_matches: '.no-prefetch, .no-prefetch a' } },
          { not: { selector_matches: '[data-skip-prefetch]' } }
        ]
      },
      eagerness: 'conservative'
    }
  ]
}

let nativeSpecRulesActive = false
/** @type {(() => void) | null} */
let quicklinkCleanup = null

const injectSpeculationRules = () => {
  const script = document.createElement('script')
  script.type = 'speculationrules'
  script.textContent = JSON.stringify(speculationRulesPayload)
  document.head.appendChild(script)
}

const startQuicklink = async () => {
  const { listen } = await import('quicklink')
  if (quicklinkCleanup) {
    quicklinkCleanup()
    quicklinkCleanup = null
  }
  quicklinkCleanup = listen({ ignores: quicklinkIgnores })
}

/**
 * Initialize prefetch: native speculation rules when supported, otherwise lazy-load quicklink.
 */
export const initPrefetch = () => {
  void (async () => {
    try {
      if (HTMLScriptElement.supports?.('speculationrules')) {
        injectSpeculationRules()
        nativeSpecRulesActive = true
        return
      }
      await startQuicklink()
    } catch (error) {
      console.debug('Prefetch init failed:', error)
    }
  })()
}

/**
 * Re-run quicklink after DOM swaps (e.g. Highway). No-op when native speculation rules are active.
 */
export const refreshPrefetch = () => {
  if (nativeSpecRulesActive) return
  void (async () => {
    try {
      await startQuicklink()
    } catch (error) {
      console.debug('Prefetch refresh failed:', error)
    }
  })()
}

/**
 * Disconnect quicklink observers (fallback path only).
 */
export const destroyPrefetch = () => {
  if (quicklinkCleanup) {
    quicklinkCleanup()
    quicklinkCleanup = null
  }
}
